import {
  Description as HeadlessDescription,
  Dialog as HeadlessDialog,
  DialogPanel as HeadlessDialogPanel,
  DialogTitle as HeadlessDialogTitle,
  Transition as HeadlessTransition,
  TransitionChild as HeadlessTransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import { Fragment } from "react";
import { Text } from "V2.0/common/text";

const sizes = {
  xs: "sm:max-w-xs",
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
  "4xl": "sm:max-w-4xl",
  "5xl": "sm:max-w-5xl",
  full: "max-w-full",
};

export function Dialog({
  open,
  onClose,
  size = "lg",
  className,
  children,
  ...props
}) {
  return (
    <HeadlessTransition
      appear
      as={Fragment}
      show={open}
      {...props}
    >
      <HeadlessDialog onClose={onClose}>
        <HeadlessTransitionChild
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 focus:outline-0 dark:bg-zinc-950/50" />
        </HeadlessTransitionChild>

        <HeadlessTransitionChild
          className={clsx(
            size === "full"
              ? "fixed inset-6 flex"
              : "fixed inset-0 w-screen overflow-y-auto",
          )}
          enter="ease-out duration-100"
          enterFrom="opacity-0 translate-y-12 sm:translate-y-0"
          enterTo="opacity-100 translate-y-0"
          leave="ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-12 sm:translate-y-0"
        >
          <div
            className={clsx(
              size === "full"
                ? "flex"
                : "grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] p-2",
            )}
          >
            <HeadlessTransitionChild
              as={HeadlessDialogPanel}
              className={clsx(
                className,
                sizes[size],
                size === "full"
                  ? "flex flex-col w-full h-full rounded-xl overflow-hidden"
                  : "row-start-2 w-full min-w-0 sm:mb-auto sm:rounded-xl",
                "bg-white p-[--gutter] shadow-lg ring-1 ring-zinc-950/10 dark:bg-zinc-900 dark:ring-white/10 forced-colors:outline",
              )}
              enter={size === 'full' ? 'duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]' : 'ease-out duration-100'}
              enterFrom={size === "full" ? "opacity-0" : "sm:scale-95"}
              enterTo={size === "full" ? "opacity-100" : "sm:scale-100"}
              leave="ease-in duration-100"
              leaveFrom={size === "full" ? "opacity-100" : "sm:scale-100"}
              leaveTo={size === "full" ? "opacity-0" : "sm:scale-100"}
            >
              {children}
            </HeadlessTransitionChild>
          </div>
        </HeadlessTransitionChild>
      </HeadlessDialog>
    </HeadlessTransition>
  );
}

export function DialogTitle({ className, ...props }) {
  return (
    <HeadlessDialogTitle
      {...props}
      className={clsx(
        className,
        "rounded-t-xl text-balance text-lg/6 font-semibold text-zinc-950 sm:text-base/6 dark:text-white border",
      )}
    />
  );
}

export function DialogDescription({ className, ...props }) {
  return (
    <HeadlessDescription
      as={Text}
      {...props}
      className={clsx(className, "mt-2 text-pretty")}
    />
  );
}

export function DialogBody({ className, ...props }) {
  return (
    <div
      {...props}
      className={clsx(className)}
    />
  );
}

export function DialogActions({ className, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        "flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row rounded-b-xl",
      )}
    />
  );
}